import {isUserLoggedIn} from '@Libraries/user.library';
import {redirectUser} from '@Utils/browser.util';
import type {VoidFunction} from '@Utils/general.util';
import {noop} from '@Utils/general.util';
import type {Editor} from 'grapesjs';
import {openAuthenticateModal} from '@Modals/authenticate-modal';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';
import {SaveActionType} from '@/email-maker/grapesjs-email-editor-plugin/src/email-maker.types';
import {EditorType} from '@Utils/misc.util';

/**
 * Storage related commands
 */
export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const SAVE_INFORMATION_TEXT_CLASS = '.js-template-save-information';
  const SPAM_DETECTED_DLG = 'spam-detected-dialog';
  const cmds = editor.Commands;

  const CMDS = window.PMW.emails.commands;

  /**
   * Returns true if the current user is premium, and false otherwise.
   * @returns {boolean}
   */
  const isUserPremium = (): boolean => {
    return window.PMW.getUserPremiumLevel() > 0;
  };

  const getFormattedSpamMessage = (spamWords: string[]): string => {
    let errorMsg;

    if (spamWords.length > 1) {
      errorMsg = `${getSpamCountMessage(spamWords)} - <a href="javascript:void(0);" class="body-xs-bold js-spam-words-info -nodecoration">${window.i18next.t('pmwjs_spam_and_more', {word: spamWords[0], number: spamWords.length - 1})}</a>`;
    } else {
      errorMsg = `${getSpamCountMessage(spamWords)} - <a href="javascript:void(0);" class="body-xs-bold js-spam-words-info -nodecoration">${spamWords[0]}</a>`;
    }

    return errorMsg.replace(/(\r\n|\n|\r)/gm, '');
  };

  const getSpamDetectedDialogHTML = (spamWords: string[]): string => {
    let spamWordsHtml = '';
    const spamCountText = getSpamCountMessage(spamWords);

    spamWords.forEach((spamWord) => {
      spamWordsHtml += `<p class="body-xs-bold _unmargin-left _unmargin-right spacing-m-t-2 spacing-m-b-2">${spamWord}</p>`;
    });

    return (
      `<div class="spacing-m-b-6">` +
      `<div class="ct">` +
      `<i class="bg-danger-10 color-danger-100 radius-round spacing-p-3 size-icon-32 icon-exclamation-triangle"></i>` +
      `</div>` +
      `<p class="body-xs _unmargin-left _unmargin-right spacing-m-t-7 spacing-m-b-5">${spamCountText}:</p>${spamWordsHtml}<p class="body-xs _unmargin-left _unmargin-right spacing-m-t-5 spacing-m-b-1">${window.i18next.t('pmwjs_please_remove_spam')}</p>` +
      `</div>`
    );
  };

  const getSpamCountMessage = (spamWords: string[]): string => {
    let spamCountText;

    if (spamWords.length === 1) {
      spamCountText = window.i18next.t('pmwjs_contained_spam_word');
    } else {
      spamCountText = window.i18next.t('pmwjs_contained_spam_words', {number: `<strong class="body-xs-bold">${spamWords.length}</strong>`});
    }

    return spamCountText;
  };

  /**
   * Shows the premium upsell dialog for PAYG users when publishing an email.
   */
  const showPremiumUpsellDialogForPublishing = (): void => {
    window.PMW.showPremiumOnlyFeatureDialog(window.PMW.PREMIUM_ONLY_FEATURE_NAME_PUBLISH_EMAIL_CAMPAIGN);
  };

  const getRelativeTimeString = (current: number, previous: number): string => {
    const msPerMinute = 60 * 1000;
    const msPerHour = msPerMinute * 60;
    const msPerDay = msPerHour * 24;
    const msPerMonth = msPerDay * 30;
    const msPerYear = msPerDay * 365;

    const elapsed = current - previous;

    if (elapsed < msPerMinute) {
      return window.i18next.t('pmwjs_saved_moments_ago');
    }
    if (elapsed < msPerHour) {
      const val = Math.round(elapsed / msPerMinute);
      if (val === 1) {
        return window.i18next.t('pmwjs_saved_a_minute_ago');
      }

      return window.i18next.t('pmwjs_saved_x_minutes_ago', {
        minutes: val,
      });
    }
    if (elapsed < msPerDay) {
      const val = Math.round(elapsed / msPerHour);
      if (val === 1) {
        return window.i18next.t('pmwjs_saved_an_hour_ago');
      }

      return window.i18next.t('pmwjs_saved_x_hours_ago', {
        hours: val,
      });
    }
    if (elapsed < msPerMonth) {
      const val = Math.round(elapsed / msPerDay);
      if (val === 1) {
        return window.i18next.t('pmwjs_saved_a_day_ago');
      }

      return window.i18next.t('pmwjs_saved_x_days_ago', {
        days: val,
      });
    }
    if (elapsed < msPerYear) {
      const val = Math.round(elapsed / msPerMonth);
      if (val === 1) {
        return window.i18next.t('pmwjs_saved_a_month_ago');
      }

      return window.i18next.t('pmwjs_saved_x_months_ago', {
        months: val,
      });
    }
    const val = Math.round(elapsed / msPerYear);
    if (val === 1) {
      return window.i18next.t('pmwjs_saved_a_year_ago');
    }

    return window.i18next.t('pmwjs_saved_x_years_ago', {
      years: val,
    });
  };

  cmds.add(CMDS.AUTHENTICATE_USER, {
    run(editorInstance: Editor, sender, opts: {onEarlyClose?: VoidFunction; cb?: VoidFunction}) {
      if (typeof opts.onEarlyClose !== 'function') {
        opts.onEarlyClose = noop;
      }

      if (window.PMW.isUserLoggedIn()) {
        if (typeof opts.cb === 'function') {
          opts.cb();
        }

        return;
      }

      openAuthenticateModal({
        fbPermissions: '',
        signupSource: 'email-editor',
        onLoginSuccess: async () => {
          editor.runCommand(CMDS.USER_LOGGED_IN);
          if (typeof opts.cb === 'function') {
            opts.cb();
          }
        },
        onCloseCb: (): void => {
          if (typeof opts.onEarlyClose === 'function') {
            opts.onEarlyClose();
          }
        },
      });
    },
  });

  cmds.add(CMDS.USER_LOGGED_IN, {
    async run(editorInstance: Editor) {
      editorInstance.runCommand(CMDS.UPDATE_PUBLISH_CTA_UI);
      if (!editorInstance.runCommand(CMDS.IS_USER_OWNER)) {
        editorInstance.runCommand(CMDS.UPDATE_SAVE_BUTTON);
        return;
      }

      editorInstance.runCommand(CMDS.SET_AUTOSAVE, {autosave: true});
      await editorInstance.runCommand(CMDS.SAVE_TO_CACHE);
      editorInstance.runCommand(CMDS.RELOAD_PREMIUM_BACKGROUNDS);
    },
  });

  cmds.add(CMDS.IS_USER_OWNER, (_) => {
    return $('#html-editor-container').attr('data-do-set-autosave-active') === '1';
  });

  /**
   * @param {object} opts
   * @param {bool} opts.autosave
   */
  cmds.add(
    CMDS.SET_AUTOSAVE,
    (
      editorInstance: Editor,
      sender,
      opts: {
        autosave: boolean;
      }
    ) => {
      if (!navigator.sendBeacon) {
        opts.autosave = false;
        console.warn('Beacon API not found, autosave will be disabled.');
      }

      editorInstance.StorageManager.setAutosave(opts.autosave);
      const autoSaveState = opts.autosave ? window.PMW.emails.AUTOSAVE_STATE_SAVED : window.PMW.emails.AUTOSAVE_STATE_NOT_SAVED;

      editorInstance.runCommand(CMDS.UPDATE_SAVE_BUTTON, {state: autoSaveState});
    }
  );

  cmds.add(CMDS.UPDATE_PUBLISH_CTA_UI, () => {
    $('.js-publish-email-btn.btn').removeClass('premium').addClass('secondary');
    $('.js-publish-email-btn.pmw-icon').removeClass('-premium').addClass('-secondary');
  });

  /**
   * @param {object} opts
   * @param {string} state one of AUTOSAVE_STATE_* constants
   */
  cmds.add(
    CMDS.UPDATE_SAVE_BUTTON,
    (
      editorInstance: Editor,
      sender,
      opts: {
        state: string;
      }
    ) => {
      const isLoggedIn = isUserLoggedIn();
      const saveIcon = $('.js-auto-save-icon');
      const saveBtn = $('.js-save-email-template');
      const saveContainer = $('.js-auto-save-container');
      const saveInformation = $('.js-template-save-information');
      const saveRetry = $('.js-save-email-template-retry');
      const isDisabled = !editorInstance.StorageManager.isAutosave();
      const isSaved = opts.state === window.PMW.emails.AUTOSAVE_STATE_SAVED;
      const isSaving = opts.state === window.PMW.emails.AUTOSAVE_STATE_SAVING;
      const notSaved = opts.state === window.PMW.emails.AUTOSAVE_STATE_NOT_SAVED;

      if (!isLoggedIn) {
        saveIcon.removeClass('_hidden');
        saveInformation.removeClass('_hidden');
        saveContainer.removeClass('_hidden');
        saveBtn.removeClass('-animate-hide');
        saveInformation.html(window.i18next.t('pmwjs_disabled'));
        return;
      }

      editorInstance.runCommand(CMDS.INIT_AUTOSAVE_UI);

      if (isDisabled && isSaved) {
        saveBtn.removeClass('-animate-hide');
        saveBtn.removeClass('-collapsed');
        saveIcon.removeClass('icon-save-loading icon-auto-saved').addClass('icon-offline');
        return;
      }

      if (isSaving) {
        saveRetry.addClass('_hidden');
        saveContainer.removeClass('-error');
        saveContainer.attr('title', window.i18next.t('pmwjs_saving'));
        saveIcon.removeClass('icon-offline icon-auto-saved').addClass('icon-save-loading');
      } else if (isSaved) {
        saveContainer.attr('title', window.i18next.t('pmwjs_saved_a_moment_ago'));
        saveIcon.removeClass('icon-offline icon-save-loading').addClass('icon-auto-saved');
        saveBtn.addClass('-animate-hide');
      }

      if (notSaved) {
        saveContainer.addClass('-error');
        saveRetry.removeClass('_hidden');
        saveContainer.attr('title', window.PMW.emails.SAVE_BUTTON_TITLE);
        saveIcon.removeClass('icon-save-loading icon-auto-saved').addClass('icon-offline');
      }
    }
  );

  cmds.add(CMDS.BEFORE_SAVE, {
    run(editorInstance: Editor) {
      if (!isUserLoggedIn()) {
        return;
      }

      const saveInfoText = $(SAVE_INFORMATION_TEXT_CLASS);
      const saveBtn = $('.js-save-email-template');

      saveInfoText.attr('data-saving', 1);
      saveInfoText.html(window.i18next.t('pmwjs_saving'));
      saveBtn.addClass('-loading');
      editorInstance.runCommand(CMDS.UPDATE_SAVE_BUTTON, {state: window.PMW.emails.AUTOSAVE_STATE_SAVING});
    },
  });

  cmds.add(CMDS.AFTER_SAVE, {
    run(editorInstance: Editor) {
      const saveInfoText = $(SAVE_INFORMATION_TEXT_CLASS);

      saveInfoText.html(window.i18next.t('pmwjs_all_changes_saved_without_period'));
      saveInfoText.attr('data-time-saved', new Date().getTime());
      saveInfoText.attr('data-saving', 0);
      editorInstance.runCommand(CMDS.HIDE_ERROR);
      editorInstance.runCommand(CMDS.ENABLE_BLOCKS);

      editorInstance.runCommand(CMDS.TRIGGER_STORAGE_END_EVENT);
      editorInstance.runCommand(CMDS.UPDATE_SAVE_BUTTON, {state: window.PMW.emails.AUTOSAVE_STATE_SAVED});
    },
  });

  cmds.add(CMDS.TRIGGER_STORAGE_END_EVENT, {
    run() {
      document.dispatchEvent(new CustomEvent(window.PMW.emails.CUSTOM_EVENT_STORAGE_END, {}));
    },
  });

  cmds.add(
    CMDS.UNABLE_TO_SAVE,
    (
      editorInstance: Editor,
      sender,
      opts: {
        code: string;
        message: string;
        data?: any;
      }
    ) => {
      const saveBtn = $('.js-save-email-template');
      const saveInfoText = $(SAVE_INFORMATION_TEXT_CLASS);

      switch (opts.code) {
        case CMDS.AUTHENTICATE_USER:
          editorInstance.runCommand(CMDS.AUTHENTICATE_USER, {
            cb: () => {
              if (isUserLoggedIn()) {
                editorInstance.runCommand(CMDS.SAVE_TO_CACHE, {});
              }
            },
            onEarlyClose: () => {
              saveBtn.removeClass('-loading');
            },
          });
          break;
        case 'content-limit':
          editorInstance.runCommand(CMDS.SHOW_ERROR, {message: opts.message});
          editorInstance.runCommand(CMDS.DISABLE_BLOCKS);
          window.PMW.openErrorModal({message: opts.message, title: window.i18next.t('pmwjs_content_limit')});
          window.PMW.emails.SAVE_BUTTON_TITLE = window.i18next.t('pmwjs_content_limit_error_not_saved');
          break;
        case 'uppercase-limit':
          editorInstance.runCommand(CMDS.SHOW_ERROR, {
            message: opts.message,
            fixForMeAction: () => {
              editorInstance.runCommand(CMDS.FIX_UPPER_CASE_LIMIT);
            },
          });
          editorInstance.runCommand(CMDS.DISABLE_BLOCKS);
          window.PMW.emails.SAVE_BUTTON_TITLE = window.i18next.t('pmwjs_uppercase_error_not_saved');
          break;
        case 'existing-save-data':
          window.PMW.emails.SAVE_BUTTON_TITLE = window.i18next.t('pmwjs_save_conflict_error');

          if (editorInstance.StorageManager.isAutosave() || window.PMW.emails.SHOULD_SHOW_SAVE_CONFLICT) {
            window.PMW.openUserSaveConflictPopup(
              document.getElementById('editor-save-conflict-popup'),
              opts.data.currentUserPictureURL,
              opts.data.otherUserPictureURL,
              opts.data.otherUserName,
              () => {
                void doSaveYours();
              },
              () => {
                void doSaveTheirs();
              },
              getEditorType()
            );

            showSaveConflictPopup();
          }

          window.PMW.emails.SHOULD_SHOW_SAVE_CONFLICT = true;

          break;
        default:
          window.PMW.openErrorModal({message: opts.message});
          window.PMW.emails.SAVE_BUTTON_TITLE = window.i18next.t('pmwjs_not_saved_detailed');
          break;
      }

      saveInfoText.html(window.i18next.t('pmwjs_not_saved_retry'));
      saveBtn.addClass('-animate-hide');
      editorInstance.runCommand(CMDS.UPDATE_SAVE_BUTTON, {state: window.PMW.emails.AUTOSAVE_STATE_NOT_SAVED});
      editorInstance.runCommand(window.PMW.emails.commands.SET_AUTOSAVE, {autosave: false});
    }
  );

  cmds.add(CMDS.START_UPDATING_SAVE_TIME, {
    run() {
      setInterval(() => {
        const saveInformationEl = $(SAVE_INFORMATION_TEXT_CLASS);
        const saveContainerTooltip = $('.js-auto-save-container');
        const timeSaved = saveInformationEl.attr('data-time-saved');
        const isSaving = saveInformationEl.attr('data-saving') && parseInt(saveInformationEl.attr('data-saving')!, 10) === 1;

        if (timeSaved && !isSaving) {
          const updatedString = getRelativeTimeString(new Date().getTime(), parseInt(timeSaved, 10));
          saveContainerTooltip.attr('title', updatedString);
        }
      }, 60000);
    },
  });

  /**
   * @returns {Promise<object|null>} saved data or null on error
   */
  cmds.add(CMDS.SAVE_TO_CACHE, {
    async run(editorInstance: Editor, sender, opts?: {saveActionType?: SaveActionType}) {
      try {
        const data = await editorInstance.store({saveActionType: opts?.saveActionType});
        this.stopCommand();
        return data;
      } catch (_) {
        return null;
      }
    },
    stop() {},
  });

  /**
   * @return {string|null} saved data as a JSON string
   */
  cmds.add(CMDS.SAVE_TO_DB, {
    async run(editorInstance: Editor) {
      if (!window.PMW.idEmailTemplate || !window.PMW.emails.HAS_SAVED) {
        this.stopCommand();
        return {};
      }

      const url = window.PMW.util.site_url('emailmaker/saveEmailTemplateToDb');
      const data = JSON.stringify({idEmailTemplate: window.PMW.idEmailTemplate});

      if (navigator.sendBeacon && editorInstance.StorageManager.isAutosave()) {
        navigator.sendBeacon(url, data);
      } else {
        await window.PMW.write(url, data);
      }

      this.stopCommand();
      return data;
    },
    stop() {},
  });

  /**
   * saves payload and then stores payload to DB
   */
  cmds.add(CMDS.SAVE_TO_DB_AND_CACHE, async (editorInstance: Editor): Promise<void> => {
    await editorInstance.runCommand(CMDS.SAVE_TO_CACHE, {});
    if (isUserLoggedIn()) {
      await editorInstance.runCommand(CMDS.SAVE_TO_DB, {
        force: true,
        forceSave: true,
      });
    }
  });

  cmds.add(CMDS.GENERATE_PREVIEW_IMAGE, {
    run() {
      if (window.PMW.idEmailTemplate) {
        void window.PMW.write(window.PMW.util.site_url(`emailmaker/generateEmailTemplatePreview/${window.PMW.idEmailTemplate}`), {});
      }
    },
  });

  cmds.add(CMDS.SAVE_AND_PUBLISH, async (editorInstance: Editor) => {
    if (!isUserLoggedIn()) {
      editorInstance.runCommand(CMDS.AUTHENTICATE_USER, {
        cb: (): void => {
          editorInstance.runCommand(CMDS.SAVE_AND_PUBLISH, {});
        },
      });
      return;
    }
    if (!isUserPremium()) {
      showPremiumUpsellDialogForPublishing();
      return;
    }

    if (window.PMW.emails.IS_SCHEDULED) {
      window.PMW.emails.PUBLISH_CTA = $('.js-schedule-email-campaign');
    }

    window.PMW.emails.PUBLISH_CTA.addClass('-loading');
    await editorInstance.runCommand(CMDS.SAVE_TO_CACHE, {});
    window.PMW.emails.PUBLISH_CTA.removeClass('-loading');

    if (window.PMW.emails.IS_SCHEDULED && window.PMW.emails.HAS_SAVED) {
      await window.PMW.write(window.PMW.util.site_url('emailmarketing/updateEmailMarketingCampaignSchedule'), {
        isScheduled: window.PMW.emails.IS_SCHEDULED,
        idEmailMarketingCampaign: window.PMW.idEmailCampaign,
        scheduledDateTime: window.PMW.emails.SCHEDULED_TIME,
      });
    }

    editorInstance.runCommand(CMDS.VALIDATE_TEMPLATE_AND_PUBLISH);
  });

  cmds.add(CMDS.VALIDATE_TEMPLATE_AND_PUBLISH, async (): Promise<void> => {
    window.PMW.emails.PUBLISH_CTA.addClass('-loading');
    redirectUser(window.PMW.util.site_url(`emailmarketing/publish/campaign/${window.PMW.idEmailCampaign}`), false, false);
    // TODO: Re-enable after re-work, causing too much user friction
    // $.get(PMW.util.site_url('emailmaker/validateEmailTemplateContent/' + window.PMW.idEmailTemplate), null, (r) => {
    //     if (r.status === 'success') {
    //         redirectUser(PMW.util.site_url('emailmarketing/publish/campaign/' + window.PMW.idEmailCampaign), false, false);
    //     } else {
    //         window.PMW.emails.PUBLISH_CTA.removeClass('-loading');
    //         if (r.code === 'spam-word') {
    //             let btn = {};
    //
    //             btn[window.i18next.t('pmwjs_close')] = {
    //                 classes: '-fullwidth _unmargin',
    //                 ch: window.PMW.closeModalDialog.bind(this, SPAM_DETECTED_DLG)
    //             };
    //
    //             let dlg = window.PMW.openModalDialog(SPAM_DETECTED_DLG, window.i18next.t('pmwjs_spam_detected'), btn, true, '-modal-400 -fullwidth-footer _unmargin', false),
    //                 content = dlg.find('.content'),
    //                 spamWords = r.data.spam;
    //
    //             content.html(getSpamDetectedDialogHTML(spamWords));
    //             editorInstance.runCommand(CMDS.SHOW_ERROR, {message: getFormattedSpamMessage(spamWords)});
    //         }
    //     }
    // });
  });

  const doSaveYours = async (): Promise<void> => {
    await doSaveWithAction(SaveActionType.ACCEPT_YOURS);
  };

  const doSaveTheirs = async (): Promise<void> => {
    await doSaveWithAction(SaveActionType.ACCEPT_THEIRS);
  };

  const doSaveWithAction = async (action: SaveActionType): Promise<void> => {
    hideSaveConflictPopup();
    await editor.runCommand(CMDS.SAVE_TO_CACHE, {saveActionType: action});
    await resetDefaultSelection();
    await editor.load();
    editor.runCommand(window.PMW.emails.commands.UPDATE_SAVE_BUTTON, {state: window.PMW.emails.AUTOSAVE_STATE_SAVED});
  };

  const resetDefaultSelection = async (): Promise<void> => {
    if (window.PMW.idEmailSignupForm && window.PMW.formmaker) {
      await editor.runCommand(window.PMW.formmaker.commands.SET_DEFAULT_COMPONENT_SELECTION);
    } else {
      await editor.runCommand(CMDS.SET_DEFAULT_SELECTION);
    }
  };

  const showSaveConflictPopup = (): void => {
    $('.js-save-conflict-popup').addClass('-visible');
    $('.js-nav-back-to-style-list').addClass('_hidden');
    if (window.PMW.isIpadScreenWidth() || window.PMW.isMobileScreenWidth()) {
      $('.js-html-editor-user-options-container').addClass('send-back');
    }
  };

  const getEditorType = (): EditorType => {
    if (window.PMW.idEmailSignupForm) {
      return EditorType.FORM;
    }

    return EditorType.EMAIL;
  };

  const hideSaveConflictPopup = (): void => {
    $('.js-save-conflict-popup').removeClass('-visible');
    $('.js-nav-back-to-style-list').removeClass('_hidden');
    $('.js-html-editor-user-options-container').removeClass('send-back');
  };
};
